/**
 * Modal — shared dialog shell. Extracts the inline pattern that was
 * copy-pasted across hr/* pages (bg-black/40 backdrop, rounded-2xl sheet,
 * bottom-sheet on mobile / centered on desktop). Closes on Esc + backdrop
 * click. Controlled via `open`.
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Tailwind max-width class (default max-w-md). */
  size?: string;
}

export function Modal({ open, onClose, title, children, size = 'max-w-md' }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`bg-white w-full ${size} rounded-2xl p-4 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold">{title}</h2>
            <button onClick={onClose} aria-label="ปิด" className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
